import React from 'react';
import { CardVariants, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import MetricCard from '../metricCard/MetricCard';
import styles from './styles/kpiMetricTile.module.scss';

/**
 * Single KPI tile: icon + label + value on a `MetricCard` shell.
 * Shared by the user-journey KPI row and any other MetricCard-based
 * KPI grids that match the Figma 5747:1706 layout.
 *
 * @param {Object} props
 * @param {import('@clockout-dev/design-elements').CardVariants | undefined} [props.variant]
 * @param {React.ReactNode} props.icon Icon element (className applied internally)
 * @param {string} props.label
 * @param {string | number} props.value Pre-formatted display value
 * @param {string} [props.valueClassName] Colour class for the value. Callers pass
 *   what `MoneyController.getMoneyColorClassName()` returns so the colour rule
 *   lives in one place. It REPLACES the tile's default ink rather than stacking
 *   on top of it (see `valueColorClassName` below), and `CardVariants.HIGHLIGHT`
 *   wins over it, since a highlight tile needs a white value to stay readable.
 * @param {string} [props.sublabel] Supporting detail under the value
 * @param {string} [props.sublabelClassName] Colour class for the sublabel, same
 *   contract as `valueClassName`. Defaults to muted grey (a caveat like "Partial
 *   data" should stay this way); pass `MoneyController.getMoneyColorClassName()`
 *   when the sublabel itself is a figure that must carry the same tone as the
 *   value (e.g. an overdue amount and day count, which must read red like the
 *   value above them).
 * @param {boolean} [props.compact=false] Smaller value type, for dense grids —
 *   five tiles across the content width leave 149px of text per tile at 1440px,
 *   where a seven-figure amount wraps mid-number at the default 26px.
 * @param {string} [props.className] Extra class on the MetricCard root
 */
function KpiMetricTile({
    variant,
    icon,
    label,
    value,
    valueClassName,
    sublabel,
    sublabelClassName,
    compact = false,
    className,
}) {
    const isHighlight = variant === CardVariants.HIGHLIGHT;
    // Exactly one colour class ever lands on the value, and the same for the
    // sublabel. The money tones in `styles/money.module.scss` are plain
    // single-class rules with no `!important`, so the tile must not also declare
    // a colour on `.value` — whichever came later in webpack's import order
    // would win. The default ink lives in `.valueInk` / `.sublabelMuted` and is
    // simply not applied when the caller supplies a tone.
    const valueColorClassName = isHighlight
        ? styles.valueHighlight
        : (valueClassName || styles.valueInk);
    const sublabelColorClassName = sublabelClassName || styles.sublabelMuted;
    const iconNode = React.isValidElement(icon)
        ? React.cloneElement(icon, {
            className: isHighlight ? styles.iconHighlight : styles.iconBadge,
        })
        : icon;

    return (
        <MetricCard
            variant={variant}
            image={iconNode}
            classNames={{ icon: isHighlight ? styles.iconBoxHighlight : styles.iconBoxReset }}
            className={className}
        >
            <div className={styles.body}>
                <Text
                    as={TextAs.SPAN}
                    size={TextSizes.SMALL}
                    className={`${styles.label} ${isHighlight ? styles.labelHighlight : ''}`}
                >
                    {label}
                </Text>
                <Text
                    as={TextAs.SPAN}
                    size={TextSizes.DISPLAY_SM}
                    className={`${styles.value} ${compact ? styles.valueCompact : ''} ${valueColorClassName}`}
                >
                    {value}
                </Text>
                {sublabel ? (
                    <Text
                        as={TextAs.SPAN}
                        size={TextSizes.SMALL}
                        className={`${styles.sublabel} ${sublabelColorClassName}`}
                    >
                        {sublabel}
                    </Text>
                ) : null}
            </div>
        </MetricCard>
    );
}

export default KpiMetricTile;
